import React, { useState, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { updateUI } from 'redux/actions/ui';
import { createStructuredSelector } from 'reselect';

// redux selectors
import {
    getFontSize as getFontSizeSelector,
    getShareContactList,
    getUI,
    getUserTimeLineReadingType,
    getFilters
} from 'redux/selectors';

// core components
import DropDown from 'components/DropDown';
import SortFilter from './TimelineFilter/SortFilter';
import DisplayFilter from './TimelineFilter/DisplayFilter';
import MoveEntryDialog from 'pages/Dialogs/MoveEntryDialog';
import DeleteEntryDialog from 'pages/Dialogs/DeleteEntryDialog';
import ChangeBackgroundDialog from 'pages/Dialogs/SettingsDialog/ChangeBackgroundDialog';
import MissionStartDateStatusDialog from 'pages/Dialogs/MissionStartDateStatusDialog';
import JournalChaptersDialog from 'pages/Dialogs/JournalChaptersDialog';
import ImportGmailDialog from 'pages/Dialogs/ImportGmailDialog';
import BulkImageUploadDialog from 'pages/Dialogs/BulkImageUploadDialog';
import EmailToJournalDialog from 'pages/Dialogs/EmailToJournalDialog';
import ImportErrorReasonsDialog from 'pages/Dialogs/ImportErrorReasonsDialog';
//import MediaLibraryDialog from 'pages/Dialogs/MediaLibraryDialog';

// Local Hook
import useImportJobsMonitor from 'hooks/useImportJobsMonitor';
import {
    ENTRY_HIDDEN,
    FONT_SIZE_TYPES,
    USER_SETTINGS,
    FULL_VIEW_TYPE,
    SUMMARY_VIEW_TYPE,
    VIEW_WIDTH_MAP,
    COMPACT_MODE,
    DEFAULT_MODE,
    EXPANDED_MODE
} from 'common/constant';
import { FONTSIZE_INPUT_VALUES } from 'pages/Settings/TabPanes/ThemePane';

// Actions
import * as EntryActions from 'redux/actions/entry';
import * as UserActions from 'redux/actions/user';
import * as ContactsActions from 'redux/actions/contact';

// icons
import iconCheck from 'assets/images/icons_svg/icon_check.svg';
import iconManage from 'assets/images/icons_svg/icon_manage-entries.svg';
import iconCaretDown from 'assets/images/icons_svg/icon_caret-down.svg';
import iconThemes from 'assets/images/icons_svg/icon_themes.svg';
import iconDelete from 'assets/images/icons_svg/icon_delete.svg';
import iconMove from 'assets/images/icons_svg/icon_move.svg';
import iconHide from 'assets/images/icons_svg/icon_hide.svg';
import iconCopy from 'assets/images/icons_svg/icon_copy.svg';
import iconMissionCalendar from 'assets/images/icons_svg/icon_mission_date.svg';
import iconEmailToJournal from 'assets/images/icons_svg/icon_mission_email.svg';
import iconBooks from 'assets/images/icons_svg/icon_mission_chapter.svg';
//import iconMediaLibrary from 'assets/images/icons_svg/icon_books.svg';
import iconUpload from 'assets/images/icons_svg/icon_mission_mbox_upload.svg';

// images
import { ListContactItem } from 'components/list_contact_item';
import CopyEntryDialog from 'pages/Dialogs/CopyEntryDialog';

const SettingsSidebar = ({
    dispatch,
    ui,
    userFontSize,
    contacts,
    reading_type,
    open,
    filters
}) => {
    const [fontSize, setFontSize] = useState(FONTSIZE_INPUT_VALUES.DEFAULT);
    const [openBulkMode, setOpenBulkMode] = useState(false);

    // Call our import job monitor hook
    useImportJobsMonitor();

    const activeJournal =
        filters && filters.filterByJournal && filters.filterByJournal.length > 0
            ? filters.filterByJournal[0]
            : null;
    const isMissionJournal =
        activeJournal &&
        (activeJournal.journal_type === 'Mission' ||
            activeJournal.is_mission ||
            activeJournal.missionjrnl_id);

    useEffect(() => {
        dispatch(ContactsActions.fetch());
    }, [dispatch]);

    useEffect(() => {
        const rhsElement = document.getElementById('drawer_right');
        if (open === true) {
            rhsElement.classList.add('show-drawer');
        } else {
            rhsElement.classList.remove('show-drawer');
        }
    }, [open]);

    const onChangeFont = useCallback(
        (e) => {
            setFontSize(e.target.value);
            dispatch(
                UserActions.update(
                    {
                        font_size: Object.keys(FONTSIZE_INPUT_VALUES).find(
                            (obj) =>
                                parseInt(FONTSIZE_INPUT_VALUES[obj]) ===
                                parseInt(e.target.value)
                        )
                    },
                    USER_SETTINGS
                )
            );
        },
        [dispatch]
    );

    const changeEntryViewMode = useCallback(
        (isSummaryView) => {
            dispatch(
                UserActions.update({
                    timeline_view_type: isSummaryView
                        ? SUMMARY_VIEW_TYPE
                        : FULL_VIEW_TYPE
                })
            );
            dispatch(
                updateUI({
                    summaryView: isSummaryView,
                    summaryViewWithEntry: {}
                })
            );
        },
        [dispatch]
    );

    const changeViewWidth = useCallback(
        (mode) => {
            dispatch(
                updateUI({
                    viewWidth: mode
                })
            );

            // call user's update
            dispatch(
                UserActions.update({
                    timeline_width: Object.keys(VIEW_WIDTH_MAP).find(
                        (key) => VIEW_WIDTH_MAP[key] === mode
                    )
                })
            );
        },
        [dispatch]
    );

    const setBulkMode = useCallback(
        (e) => {
            const mode = Boolean(Number(e.target.dataset.mode));
            setOpenBulkMode(mode);
            dispatch(
                updateUI({
                    bulkMode: mode,
                    bulkEntries: []
                })
            );
        },
        [dispatch]
    );

    useEffect(() => {
        !ui.bulkMode && setOpenBulkMode(false);
    }, [ui.bulkMode]);

    // Reload the page after a successful Gmail import so new entries appear
    useEffect(() => {
        if (
            ui.gmailJobResult &&
            !ui.gmailJobResult.isError &&
            !ui.gmailJobResult.restored
        ) {
            const timer = setTimeout(() => {
                window.location.reload();
            }, 1500);
            return () => clearTimeout(timer);
        }
    }, [ui.gmailJobResult]);

    // Reload the page after a successful Bulk Image import so new entries appear
    useEffect(() => {
        if (
            ui.bulkImageJobResult &&
            !ui.bulkImageJobResult.isError &&
            !ui.bulkImageJobResult.restored
        ) {
            const timer = setTimeout(() => {
                window.location.reload();
            }, 1500);
            return () => clearTimeout(timer);
        }
    }, [ui.bulkImageJobResult]);

    const openModal = useCallback(
        (modal) => {
            if (ui.bulkEntries?.length > 0) {
                const modal_ =
                    modal === 'move'
                        ? MoveEntryDialog
                        : modal === 'copy'
                        ? CopyEntryDialog
                        : DeleteEntryDialog;
                dispatch(
                    updateUI({
                        dialogs: [modal_]
                    })
                );
            }
        },
        [dispatch, ui.bulkEntries]
    );

    const hideEntries = useCallback(() => {
        ui.bulkEntries.map((entry) => {
            return dispatch(
                EntryActions.patch({ state: ENTRY_HIDDEN }, entry.id)
            );
        });
    }, [ui.bulkEntries, dispatch]);

    const openBackgroundDialog = useCallback(() => {
        dispatch(
            updateUI({
                dialogs: [ChangeBackgroundDialog]
            })
        );
    }, [dispatch]);

    /* const openMediaLibraryDialog = useCallback(() => {
        dispatch(
            updateUI({
                dialogs: [MediaLibraryDialog]
            })
        );
    }, [dispatch]);*/

    const openStartDateStatusModal = useCallback(() => {
        dispatch(
            updateUI({
                dialogs: [
                    (props) => (
                        <MissionStartDateStatusDialog
                            {...props}
                            journal={activeJournal}
                        />
                    )
                ]
            })
        );
    }, [dispatch, activeJournal]);

    const openChaptersModal = useCallback(() => {
        dispatch(
            updateUI({
                dialogs: [
                    (props) => (
                        <JournalChaptersDialog
                            {...props}
                            journal={activeJournal}
                        />
                    )
                ]
            })
        );
    }, [dispatch, activeJournal]);

    const openImportGmailModal = useCallback(() => {
        dispatch(
            updateUI({
                dialogs: [
                    (props) => (
                        <ImportGmailDialog {...props} journal={activeJournal} />
                    )
                ]
            })
        );
    }, [dispatch, activeJournal]);

    const openEmailToJournalModal = useCallback(() => {
        dispatch(
            updateUI({
                dialogs: [
                    (props) => (
                        <EmailToJournalDialog
                            {...props}
                            journal={activeJournal}
                        />
                    )
                ]
            })
        );
    }, [dispatch, activeJournal]);

    const openBulkImageUploadModal = useCallback(() => {
        dispatch(
            updateUI({
                dialogs: [
                    (props) => (
                        <BulkImageUploadDialog
                            {...props}
                            journal={activeJournal}
                        />
                    )
                ]
            })
        );
    }, [dispatch, activeJournal]);
    const renderReadingType = useCallback(() => {
        return (
            <>
                {open && (
                    <div className="form-group__item form-group__item--6">
                        <label>Entries</label>
                        <DropDown
                            className="form-group__select"
                            id="readingTypeModal"
                            data-toggle="dropdown"
                            aria-haspopup="true"
                            aria-expanded="false"
                            anchor={
                                <React.Fragment>
                                    <span className="form-group__select-text">
                                        {reading_type === SUMMARY_VIEW_TYPE
                                            ? 'Summary'
                                            : 'Full View'}
                                    </span>
                                    <img src={iconCaretDown} alt="" />
                                </React.Fragment>
                            }
                            dropDownMenuProps={{
                                'aria-labelledby': 'readingTypeModal',
                                style: { width: 'auto' }
                            }}
                        >
                            <li
                                onClick={() => changeEntryViewMode(false)}
                                className={`${
                                    reading_type === FULL_VIEW_TYPE &&
                                    'selected'
                                }`}
                            >
                                Full View
                            </li>
                            <li
                                onClick={() => changeEntryViewMode(true)}
                                className={`${
                                    reading_type === SUMMARY_VIEW_TYPE &&
                                    'selected'
                                }`}
                            >
                                Summary
                            </li>
                        </DropDown>
                    </div>
                )}
            </>
        );
    }, [changeEntryViewMode, reading_type, open]);
    const renderReadingView = useCallback(() => {
        const views = [
            { id: COMPACT_MODE, name: 'Compact' },
            { id: DEFAULT_MODE, name: 'Default' },
            { id: EXPANDED_MODE, name: 'Expanded' }
        ];

        const selectedView = (
            views.find((view) => view.id === ui.viewWidth) || views[1]
        ).name;

        return (
            <div className="form-group__item form-group__item--6">
                <label>Reading Width</label>
                <DropDown
                    className="form-group__select"
                    id="readingViewModal"
                    data-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                    dropDownMenuProps={{
                        'aria-labelledby': 'readingViewModal',
                        style: { width: 'auto' }
                    }}
                    anchor={
                        <React.Fragment>
                            <span className="form-group__select-text">
                                {selectedView}
                            </span>
                            <img src={iconCaretDown} alt="" />
                        </React.Fragment>
                    }
                >
                    {views.map((view, index) => (
                        <li
                            key={index}
                            onClick={() => changeViewWidth(view.id)}
                            className={`${
                                ui.viewWidth === view.id ? 'selected' : ''
                            }`}
                        >
                            {view.name}
                        </li>
                    ))}
                </DropDown>
            </div>
        );
    }, [changeViewWidth, ui.viewWidth]);

    // filter by contact handler
    useEffect(() => {
        setFontSize(
            FONTSIZE_INPUT_VALUES[userFontSize || FONT_SIZE_TYPES.DEFAULT]
        );
    }, [userFontSize]);
    return (
        <div
            className="drawer drawer--right"
            style={{ right: 0, position: 'fixed' }}
            id="drawer_right"
        >
            <div className="drawer__content">
                <div className="drawer__section">
                    {isMissionJournal && !openBulkMode && (
                        <>
                            <p className="drawer-section-heading">
                                Mission JRNL Options
                            </p>
                            <button
                                className="btn-drawer btn-drawer--manage"
                                onClick={openStartDateStatusModal}
                            >
                                <img
                                    src={iconMissionCalendar}
                                    alt=""
                                    className="left"
                                />
                                Start date and status
                            </button>
                            <button
                                className="btn-drawer btn-drawer--manage"
                                onClick={openEmailToJournalModal}
                            >
                                <img
                                    src={iconEmailToJournal}
                                    alt=""
                                    className="left"
                                />
                                Email to journal
                            </button>
                            <button
                                className="btn-drawer btn-drawer--manage"
                                onClick={openChaptersModal}
                            >
                                <img src={iconBooks} alt="" className="left" />
                                Journal chapters
                            </button>
                            {/* Gmail import button + inline spinner */}
                            <div className="gmail-import-row">
                                <button
                                    className="btn-drawer btn-drawer--manage"
                                    onClick={openImportGmailModal}
                                    disabled={!!ui.gmailImportInProgress}
                                    title={
                                        ui.gmailImportInProgress
                                            ? 'An import is already in progress'
                                            : undefined
                                    }
                                    style={
                                        ui.gmailImportInProgress
                                            ? {
                                                  opacity: 0.55,
                                                  cursor: 'not-allowed',
                                                  flex: 1
                                              }
                                            : { flex: 1 }
                                    }
                                >
                                    <img
                                        src={iconUpload}
                                        alt=""
                                        className="left"
                                    />
                                    Upload Gmail .Mbox File
                                </button>
                                {ui.gmailImportInProgress && (
                                    <span
                                        className="gmail-import-spinner"
                                        title="Processing emails…"
                                    />
                                )}
                            </div>
                            {/* Result popup when processing completes */}
                            {ui.gmailJobResult &&
                                ui.gmailJobResult.journalId ===
                                    activeJournal?.id && (
                                    <div
                                        className={`gmail-result-popup ${
                                            ui.gmailJobResult.isError
                                                ? 'gmail-result-popup--error'
                                                : 'gmail-result-popup--success'
                                        }`}
                                    >
                                        <div className="gmail-result-popup__icon">
                                            {ui.gmailJobResult.isError
                                                ? '⚠'
                                                : '✓'}
                                        </div>
                                        <div className="gmail-result-popup__body">
                                            {ui.gmailJobResult.isError ? (
                                                <p className="gmail-result-popup__title">
                                                    Import Failed
                                                </p>
                                            ) : (
                                                <p className="gmail-result-popup__title">
                                                    Entries Imported
                                                    Successfully
                                                </p>
                                            )}
                                            <p className="gmail-result-popup__counts">
                                                <span className="gmail-result-popup__count gmail-result-popup__count--success">
                                                    {ui.gmailJobResult.success}{' '}
                                                    imported
                                                </span>
                                                {ui.gmailJobResult.skipped >
                                                    0 && (
                                                    <span
                                                        className="gmail-result-popup__count gmail-result-popup__count--warn"
                                                        style={{
                                                            cursor: 'pointer',
                                                            textDecoration:
                                                                'underline'
                                                        }}
                                                        onClick={() => {
                                                            dispatch(
                                                                updateUI({
                                                                    dialogs: [
                                                                        (
                                                                            props
                                                                        ) => (
                                                                            <ImportErrorReasonsDialog
                                                                                {...props}
                                                                                title="Import Details"
                                                                                sections={[
                                                                                    {
                                                                                        title: `Failed (${ui.gmailJobResult.failed})`,
                                                                                        data: ui
                                                                                            .gmailJobResult
                                                                                            .failedReasons,
                                                                                        color: '#c0392b'
                                                                                    },
                                                                                    {
                                                                                        title: `Skipped (${ui.gmailJobResult.skipped})`,
                                                                                        data: ui
                                                                                            .gmailJobResult
                                                                                            .skippedReasons,
                                                                                        color: '#856404'
                                                                                    }
                                                                                ]}
                                                                            />
                                                                        )
                                                                    ]
                                                                })
                                                            );
                                                        }}
                                                    >
                                                        {
                                                            ui.gmailJobResult
                                                                .skipped
                                                        }{' '}
                                                        skipped
                                                    </span>
                                                )}
                                                {ui.gmailJobResult.failed >
                                                    0 && (
                                                    <span
                                                        className="gmail-result-popup__count gmail-result-popup__count--error"
                                                        style={{
                                                            cursor: 'pointer',
                                                            textDecoration:
                                                                'underline'
                                                        }}
                                                        onClick={() => {
                                                            dispatch(
                                                                updateUI({
                                                                    dialogs: [
                                                                        (
                                                                            props
                                                                        ) => (
                                                                            <ImportErrorReasonsDialog
                                                                                {...props}
                                                                                title="Import Details"
                                                                                sections={[
                                                                                    {
                                                                                        title: `Failed (${ui.gmailJobResult.failed})`,
                                                                                        data: ui
                                                                                            .gmailJobResult
                                                                                            .failedReasons,
                                                                                        color: '#c0392b'
                                                                                    },
                                                                                    {
                                                                                        title: `Skipped (${ui.gmailJobResult.skipped})`,
                                                                                        data: ui
                                                                                            .gmailJobResult
                                                                                            .skippedReasons,
                                                                                        color: '#856404'
                                                                                    }
                                                                                ]}
                                                                            />
                                                                        )
                                                                    ]
                                                                })
                                                            );
                                                        }}
                                                    >
                                                        {
                                                            ui.gmailJobResult
                                                                .failed
                                                        }{' '}
                                                        failed
                                                    </span>
                                                )}
                                            </p>
                                            {ui.gmailJobResult.isError &&
                                                ui.gmailJobResult
                                                    .errorDetails && (
                                                    <p className="gmail-result-popup__detail">
                                                        {
                                                            ui.gmailJobResult
                                                                .errorDetails
                                                        }
                                                    </p>
                                                )}
                                        </div>
                                        <button
                                            className="gmail-result-popup__dismiss"
                                            onClick={() => {
                                                localStorage.removeItem(
                                                    'activeGmailJob'
                                                );
                                                dispatch(
                                                    updateUI({
                                                        gmailJobResult: null
                                                    })
                                                );
                                            }}
                                            aria-label="Dismiss"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                )}
                            {/* Bulk Image Upload button + inline spinner */}
                            <div className="gmail-import-row">
                                <button
                                    className="btn-drawer btn-drawer--manage"
                                    onClick={openBulkImageUploadModal}
                                    disabled={!!ui.bulkImageImportInProgress}
                                    title={
                                        ui.bulkImageImportInProgress
                                            ? 'A bulk image upload is already in progress'
                                            : undefined
                                    }
                                    style={
                                        ui.bulkImageImportInProgress
                                            ? {
                                                  opacity: 0.55,
                                                  cursor: 'not-allowed',
                                                  flex: 1
                                              }
                                            : { flex: 1 }
                                    }
                                >
                                    <img
                                        src={iconUpload}
                                        alt=""
                                        className="left"
                                    />
                                    Bulk Image Upload
                                </button>
                                {ui.bulkImageImportInProgress && (
                                    <span
                                        className="gmail-import-spinner"
                                        title="Processing images…"
                                    />
                                )}
                            </div>
                            {/* Result popup when bulk image processing completes */}
                            {ui.bulkImageJobResult &&
                                ui.bulkImageJobResult.journalId ===
                                    activeJournal?.id && (
                                    <div
                                        className={`gmail-result-popup ${
                                            ui.bulkImageJobResult.isError
                                                ? 'gmail-result-popup--error'
                                                : 'gmail-result-popup--success'
                                        }`}
                                    >
                                        <div className="gmail-result-popup__icon">
                                            {ui.bulkImageJobResult.isError
                                                ? '⚠'
                                                : '✓'}
                                        </div>
                                        <div className="gmail-result-popup__body">
                                            {ui.bulkImageJobResult.isError ? (
                                                <p className="gmail-result-popup__title">
                                                    Image Import Failed
                                                </p>
                                            ) : (
                                                <p className="gmail-result-popup__title">
                                                    Images Imported Successfully
                                                </p>
                                            )}
                                            <p className="gmail-result-popup__counts">
                                                <span className="gmail-result-popup__count gmail-result-popup__count--success">
                                                    {
                                                        ui.bulkImageJobResult
                                                            .success
                                                    }{' '}
                                                    imported
                                                </span>
                                                {ui.bulkImageJobResult.failed >
                                                    0 && (
                                                    <span
                                                        className="gmail-result-popup__count gmail-result-popup__count--error"
                                                        style={{
                                                            cursor: 'pointer',
                                                            textDecoration:
                                                                'underline'
                                                        }}
                                                        onClick={() => {
                                                            dispatch(
                                                                updateUI({
                                                                    dialogs: [
                                                                        (
                                                                            props
                                                                        ) => (
                                                                            <ImportErrorReasonsDialog
                                                                                {...props}
                                                                                title="Import Details"
                                                                                sections={[
                                                                                    {
                                                                                        title: `Failed (${ui.bulkImageJobResult.failed})`,
                                                                                        data: ui
                                                                                            .bulkImageJobResult
                                                                                            .failedReasons,
                                                                                        color: '#c0392b'
                                                                                    },
                                                                                    {
                                                                                        title: `Skipped (${ui.bulkImageJobResult.skipped})`,
                                                                                        data: ui
                                                                                            .bulkImageJobResult
                                                                                            .skippedReasons,
                                                                                        color: '#856404'
                                                                                    }
                                                                                ]}
                                                                            />
                                                                        )
                                                                    ]
                                                                })
                                                            );
                                                        }}
                                                    >
                                                        {
                                                            ui
                                                                .bulkImageJobResult
                                                                .failed
                                                        }{' '}
                                                        failed
                                                    </span>
                                                )}
                                                {ui.bulkImageJobResult.skipped >
                                                    0 && (
                                                    <span
                                                        className="gmail-result-popup__count gmail-result-popup__count--warn"
                                                        style={{
                                                            cursor: 'pointer',
                                                            textDecoration:
                                                                'underline'
                                                        }}
                                                        onClick={() => {
                                                            dispatch(
                                                                updateUI({
                                                                    dialogs: [
                                                                        (
                                                                            props
                                                                        ) => (
                                                                            <ImportErrorReasonsDialog
                                                                                {...props}
                                                                                title="Import Details"
                                                                                sections={[
                                                                                    {
                                                                                        title: `Failed (${ui.bulkImageJobResult.failed})`,
                                                                                        data: ui
                                                                                            .bulkImageJobResult
                                                                                            .failedReasons,
                                                                                        color: '#c0392b'
                                                                                    },
                                                                                    {
                                                                                        title: `Skipped (${ui.bulkImageJobResult.skipped})`,
                                                                                        data: ui
                                                                                            .bulkImageJobResult
                                                                                            .skippedReasons,
                                                                                        color: '#856404'
                                                                                    }
                                                                                ]}
                                                                            />
                                                                        )
                                                                    ]
                                                                })
                                                            );
                                                        }}
                                                    >
                                                        {
                                                            ui
                                                                .bulkImageJobResult
                                                                .skipped
                                                        }{' '}
                                                        skipped
                                                    </span>
                                                )}
                                            </p>
                                            {ui.bulkImageJobResult.isError &&
                                                ui.bulkImageJobResult
                                                    .errorDetails && (
                                                    <p className="gmail-result-popup__detail">
                                                        {
                                                            ui
                                                                .bulkImageJobResult
                                                                .errorDetails
                                                        }
                                                    </p>
                                                )}
                                        </div>
                                        <button
                                            className="gmail-result-popup__dismiss"
                                            onClick={() => {
                                                localStorage.removeItem(
                                                    'activeBulkImageJob'
                                                );
                                                dispatch(
                                                    updateUI({
                                                        bulkImageJobResult: null
                                                    })
                                                );
                                            }}
                                            aria-label="Dismiss"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                )}
                        </>
                    )}

                    {openBulkMode ? (
                        <button
                            data-mode={0}
                            className="btn btn-gradient btn-finish"
                            onClick={setBulkMode}
                        >
                            Finish Editing
                            <img src={iconCheck} alt="" className="right" />
                        </button>
                    ) : (
                        <>
                            <p className="drawer-section-heading drawer-section-heading--spaced">
                                Display Options
                            </p>
                            <button
                                data-mode={1}
                                className="btn-drawer btn-drawer--manage"
                                onClick={setBulkMode}
                            >
                                <img src={iconManage} alt="" className="left" />
                                Manage Entries
                            </button>
                            <button
                                className="btn-drawer btn-drawer--background"
                                onClick={openBackgroundDialog}
                            >
                                <img src={iconThemes} alt="" className="left" />
                                Change Background
                            </button>
                        </>
                    )}

                    {openBulkMode && (
                        <>
                            <ul className="manage-entries-list">
                                <li className="active-item">
                                    <button
                                        className="manage-entries-list__btn"
                                        onClick={hideEntries}
                                    >
                                        <img src={iconHide} alt="" />
                                        Hide{' '}
                                        {ui.bulkEntries?.length <= 1
                                            ? 'Entry'
                                            : `${ui.bulkEntries?.length} Entries`}
                                    </button>
                                </li>
                                <li>
                                    <button
                                        className="manage-entries-list__btn"
                                        onClick={() => openModal('move')}
                                    >
                                        <img src={iconMove} alt="" />
                                        Move{' '}
                                        {ui.bulkEntries?.length <= 1
                                            ? 'Entry'
                                            : `${ui.bulkEntries?.length} Entries`}
                                    </button>
                                </li>
                                <li>
                                    <button
                                        className="manage-entries-list__btn"
                                        onClick={() => openModal('copy')}
                                    >
                                        <img src={iconCopy} alt="" />
                                        Copy{' '}
                                        {ui.bulkEntries?.length <= 1
                                            ? 'Entry'
                                            : `${ui.bulkEntries?.length} Entries`}
                                    </button>
                                </li>
                                <li>
                                    <button
                                        className="manage-entries-list__btn"
                                        onClick={() => openModal('delete')}
                                    >
                                        <img src={iconDelete} alt="" />
                                        Delete{' '}
                                        {ui.bulkEntries?.length <= 1
                                            ? 'Entry'
                                            : `${ui.bulkEntries?.length} Entries`}
                                    </button>
                                </li>
                            </ul>
                        </>
                    )}
                </div>

                <div className="drawer__section">
                    <div className="form-group">
                        <div className="form-group__row">
                            {renderReadingView()}
                            {renderReadingType()}
                        </div>
                    </div>

                    <SortFilter />
                    <DisplayFilter />

                    <div className="form-group form-group--text-size">
                        <div className="form-group__item">
                            <label>Text Size</label>
                            <div className="font-slide-container w-100">
                                <ul className="font-size-list-indicators">
                                    <li className="light-font">Aa</li>
                                    <li className="medium-font">Aa</li>
                                    <li className="strong-font">Aa</li>
                                </ul>
                                <input
                                    type="range"
                                    min={FONTSIZE_INPUT_VALUES.SMALL}
                                    max={FONTSIZE_INPUT_VALUES.LARGE}
                                    value={fontSize}
                                    onChange={onChangeFont}
                                    className="font-slider"
                                    id="fontRange"
                                    step={
                                        FONTSIZE_INPUT_VALUES.LARGE -
                                        FONTSIZE_INPUT_VALUES.DEFAULT
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="drawer__section">
                    <div className="drawer__heading">
                        <div className="drawer__title">Contacts</div>
                    </div>
                    <div className="list list--contacts">
                        {contacts?.map((contact) => (
                            <ListContactItem
                                key={contact.id}
                                contact={contact}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

const state = createStructuredSelector({
    userFontSize: getFontSizeSelector,
    ui: getUI,
    contacts: getShareContactList,
    reading_type: getUserTimeLineReadingType,
    filters: getFilters
});

export default connect(state)(SettingsSidebar);
